/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Quick Schedule Days Dropdown
 *
 * @file QuickScheduleDaysDropdown.jsx
 * @author Anuja Gaikwad
 * @since 2.0.3
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Controller } from 'react-hook-form';
import {
  Select,
  MenuItem,
  FormControl
} from '@mui/material';
import { styled } from '@mui/material/styles';
import mixins from '../../assets/styles/mixins.scss';
import CommonStyles from '../../assets/styles/CommonStyles';
import { createScopedClasses, createScopedStyles } from '../../assets/styles/createScopedStyles';
import Icon from '../icons/Icon';
import {
  QUICK_SCHEDULE_FORM_DAYS_FIELD_CLASS,
  quickScheduleFormFieldLabelStyles
} from './quickScheduleFormConstants';

const renderMenuItemCheckIcon = (isSelected, classes) => {
  if (isSelected) {
    return <Icon name="TickIcon" className={classes.menuItemCheckIcon} aria-hidden />;
  }

  return (
    <span
      className={`${classes.menuItemCheckIcon} ${classes.menuItemCheckIconPlaceholder}`}
      aria-hidden
    />
  );
};

const quickScheduleDaysDropdownStyles = {
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    width: '100%',
    maxWidth: 448,

    [`&.${QUICK_SCHEDULE_FORM_DAYS_FIELD_CLASS}`]: {
      '& $label': quickScheduleFormFieldLabelStyles(mixins),
      '& $selectRoot': {
        boxShadow: 'none',
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: mixins.amethystColorV2
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: mixins.amethystColorV2
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: mixins.amethystColorV2,
          borderWidth: 1
        },
        '& .MuiSelect-select:focus': {
          backgroundColor: mixins.chalkWhiteFont,
          outline: 'none'
        }
      }
    }
  },
  label: {
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight500,
    fontFamily: mixins.fontFamilyPlusSans,
    color: mixins.color_text_primary,
    lineHeight: '24px'
  },
  formControl: {
    width: '100%'
  },
  selectRoot: {
    height: 48,
    borderRadius: 4,
    backgroundColor: mixins.chalkWhiteFont,
    cursor: 'pointer',
    '& .MuiSelect-select.MuiSelect-outlined.MuiInputBase-input.MuiOutlinedInput-input': {
      display: 'flex',
      alignItems: 'center',
      padding: '12px',
      paddingRight: 12,
      minHeight: 24,
      cursor: 'pointer'
    }
  },
  selectValueRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    gap: 8
  },
  selectValueText: {
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight400,
    fontFamily: mixins.fontFamilyPlusSans,
    color: '#2E2E2E',
    lineHeight: '24px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
    minWidth: 0
  },
  placeholderText: {
    color: '#2E2E2E'
  },
  dropdownIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: 24,
    height: 24,
    '& svg': {
      fontSize: 12,
      width: 10,
      height: 6
    }
  },
  menuPaper: {
    ...CommonStyles().scrollContainer({
      scrollbarWidth: 8,
      scrollbarColor: 'rgba(81, 46, 171, 0.15) transparent',
      borderRadius: 4
    })
  },
  menuPopoverRoot: {
    cursor: 'default'
  },
  quickScheduleMenuPaper: {
    boxShadow: mixins.paperElevation8BoxShadow,
    '--Paper-shadow': mixins.paperElevation8BoxShadow,
    '& .MuiMenuItem-root': {
      '&:hover': {
        backgroundColor: mixins.quaternary
      },
      '&.Mui-selected': {
        backgroundColor: `${mixins.quaternary} !important`
      },
      '&.Mui-selected:hover': {
        backgroundColor: `${mixins.quaternary} !important`
      }
    }
  },
  menuItem: {
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight400,
    fontFamily: mixins.fontFamilyPlusSans,
    lineHeight: '24px',
    padding: 12,
    '& .MuiListItemIcon-root': {
      display: 'none'
    },
    '&:hover': {
      backgroundColor: mixins.quaternary
    },
    '&.Mui-selected': {
      backgroundColor: `${mixins.quaternary} !important`,
      fontWeight: mixins.fontWeight600
    },
    '&.Mui-selected:hover': {
      backgroundColor: `${mixins.quaternary} !important`
    }
  },
  menuItemRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    minWidth: 0,
    gap: 8
  },
  menuItemLabel: {
    flex: '1 0 0',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  menuItemCheckIcon: {
    width: 18,
    height: 18,
    fontSize: 18,
    flexShrink: 0,
    display: 'block',
    color: mixins.color_text_primary,
    pointerEvents: 'none'
  },
  menuItemCheckIconPlaceholder: {
    visibility: 'hidden'
  }
};

const classes = createScopedClasses(
  'aet-quick-schedule-days-dropdown',
  quickScheduleDaysDropdownStyles
);

const scopedStyles = createScopedStyles(quickScheduleDaysDropdownStyles, classes);

const QuickScheduleDaysDropdownStylesRoot = styled('div')(scopedStyles);

const getSelectedLabels = (selectedKeys, daysOfWeek) => {
  if (!selectedKeys?.length) {
    return '';
  }

  return daysOfWeek
    .filter((day) => selectedKeys.includes(day.key))
    .map((day) => day.label)
    .join(', ');
};

const QuickScheduleDaysDropdown = ({
  name,
  control,
  id,
  label,
  placeholder,
  daysOfWeek,
  requiredErrorMessage,
  onChange: onChangeCallback
}) => {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        validate: (value) => (
          value?.length > 0 ? true : requiredErrorMessage
        )
      }}
      render={({ field }) => {
        const selectedLabels = getSelectedLabels(field.value, daysOfWeek);
        const displayText = selectedLabels || placeholder;

        const handleChange = (event) => {
          const nextValue = event.target.value;

          field.onChange(nextValue);

          if (onChangeCallback) {
            onChangeCallback(nextValue);
          }
        };

        const handleClose = (_event, reason) => {
          if (reason === 'selectOption') {
            return;
          }

          setMenuOpen(false);
        };

        return (
          <QuickScheduleDaysDropdownStylesRoot
            className={`${classes.wrapper} ${QUICK_SCHEDULE_FORM_DAYS_FIELD_CLASS}`}
          >
            {label && (
              <label htmlFor={id} className={classes.label}>
                {label}
              </label>
            )}
            <FormControl variant="outlined" className={classes.formControl}>
              <Select
                id={id}
                multiple
                open={menuOpen}
                onOpen={() => setMenuOpen(true)}
                onClose={handleClose}
                value={field.value || []}
                onChange={handleChange}
                onBlur={field.onBlur}
                displayEmpty
                className={classes.selectRoot}
                IconComponent={() => null}
                renderValue={() => (
                  <span className={classes.selectValueRow}>
                    <span
                      className={`${classes.selectValueText} ${!selectedLabels ? classes.placeholderText : ''}`}
                    >
                      {displayText}
                    </span>
                    <span className={classes.dropdownIcon} aria-hidden="true">
                      <Icon name="DropdownCaretDownIcon" />
                    </span>
                  </span>
                )}
                MenuProps={{
                  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                  transformOrigin: { vertical: 'top', horizontal: 'left' },
                  disablePortal: true,
                  PopoverClasses: {
                    root: classes.menuPopoverRoot
                  },
                  PaperProps: {
                    className: `${classes.menuPaper} ${classes.quickScheduleMenuPaper}`
                  },
                  MenuListProps: {
                    style: {
                      paddingTop: 0,
                      paddingBottom: 0
                    }
                  }
                }}
              >
                {daysOfWeek.map((day) => {
                  const isSelected = (field.value || []).includes(day.key);

                  return (
                    <MenuItem
                      key={day.key}
                      value={day.key}
                      className={classes.menuItem}
                    >
                      <span className={classes.menuItemRow}>
                        <span className={classes.menuItemLabel}>{day.label}</span>
                        {renderMenuItemCheckIcon(isSelected, classes)}
                      </span>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </QuickScheduleDaysDropdownStylesRoot>
        );
      }}
    />
  );
};

QuickScheduleDaysDropdown.propTypes = {
  name: PropTypes.string.isRequired,
  control: PropTypes.object.isRequired,
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  daysOfWeek: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ),
  requiredErrorMessage: PropTypes.string,
  onChange: PropTypes.func
};

QuickScheduleDaysDropdown.defaultProps = {
  label: '',
  placeholder: 'Choose',
  daysOfWeek: [],
  requiredErrorMessage: '',
  onChange: null
};

export default QuickScheduleDaysDropdown;
